import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';

export type CampaignReviewPlatform = 'google' | 'meta';
export type CampaignReviewDecision = 'ours' | 'not_ours';

export interface CampaignReviewReason {
  signal: string;
  detail: string;
  points: number;
}

export interface CampaignReviewItem {
  id: string;
  platform: CampaignReviewPlatform;
  campaignName: string;
  verdict: 'keep' | 'skip' | 'unsure';
  source: 'rule' | 'staff' | 'launched_here';
  reasons: CampaignReviewReason[];
  decidedAt: string | null;
  /** A staff decision the next sync has not applied yet. */
  pending: boolean;
  /** Name of the practice whose dashboard launched this campaign, when it is not this one. */
  launchedElsewhere: string | null;
  /** Platform status at the last sync. Missing on an older backend. */
  platformStatus?: 'active' | 'paused' | 'ended' | null;
  /** Spend over the last 90 days, when it was checked. Missing on an older backend. */
  spend90d?: number | null;
}

export interface CampaignReview {
  visible: boolean;
  /** Campaigns that need a decision. Excludes `pausedNoSpend`. */
  unsure: CampaignReviewItem[];
  /** Unsure campaigns that are paused with no spend in 90 days. Missing on an older backend: treat as empty. */
  pausedNoSpend?: CampaignReviewItem[];
  skipped: CampaignReviewItem[];
  markedOurs: CampaignReviewItem[];
  waiting: CampaignReviewItem[];
}

interface DecisionResult {
  queued: boolean;
  /** The same decision was already saved; nothing changed and no sync was queued. */
  unchanged: boolean;
  platform: CampaignReviewPlatform;
}

const base = (practiceId: string, locationId: string) =>
  `/agency/practices/${practiceId}/locations/${locationId}/ad-campaign-matches`;

export const campaignReviewApi = {
  get: async (practiceId: string, locationId: string): Promise<CampaignReview> => {
    const { data } = await apiClient.get<ApiResponse<CampaignReview>>(base(practiceId, locationId));
    return data.data;
  },

  decide: async (
    practiceId: string,
    locationId: string,
    matchId: string,
    decision: CampaignReviewDecision,
  ): Promise<DecisionResult> => {
    const { data } = await apiClient.post<ApiResponse<DecisionResult>>(
      `${base(practiceId, locationId)}/${matchId}/decision`,
      { decision },
    );
    return data.data;
  },
};
